import { FlexLayout, Typography } from '@eduact/ed-system';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { IMcqAnswer, Options, Test } from '../TestSummary.types';
import { QuestionContentWrapper, Text } from '../OrderingAnswer/OrderingAnswer';
import { FeedbacktWrapper } from '../GapAnswer/GapAnswer';
import { Spacer } from '@Styled/utilities';
import { Color, Colors } from '@Styled/theme';

type McqProps = {
	question: IMcqAnswer;
	test: Test | undefined;
	status: string | undefined;
};

const McqAnswer: React.FC<McqProps> = ({ question, test, status }) => {
	const showCorrectAnswer = useMemo(() => {
		return status === 'passed' && test?.show_correct_if_passed === true;
	}, []);

	const isStudentFailedRightAnswer = useMemo(() => {
		return status === 'failed' && test?.show_correct_if_failed === true;
	}, []);

	// Non-array guard: a question type-flipped from mrq leaves ARRAY-shaped
	// content behind; treating it as an mcq entry would fabricate an orphan
	// row with an undefined answer text.
	const rawStored = question?.answer?.content?.options;
	const stored = rawStored && !Array.isArray(rawStored) ? rawStored : null;
	// Ids identify options; texts are display snapshots. Compare ids only when
	// BOTH sides carry one — undefined === undefined is true and would mark
	// every option as the student's answer. Text keeps legacy rows working.
	const isChosen = (item: Options) => {
		if (!stored) return false;
		if (stored.option_id != null && item.id != null)
			return stored.option_id === item.id;
		return stored.answer === item.choice;
	};

	const getMcqColor = (item: Options) => {
		if (isChosen(item))
			if (stored?.correct) return 'rgba(0, 214, 107, 0.1)';
			else return '#ffd5cc';
		if (showCorrectAnswer || isStudentFailedRightAnswer)
			if (item?.is_correct) return 'rgba(0, 214, 107, 0.1)';
			else return '';
		return '#FFF';
	};
	const bulletColor = (item: Options): Color => {
		if (isChosen(item))
			if (stored?.correct) return 'green';
			else return 'lightRed';
		if (showCorrectAnswer || isStudentFailedRightAnswer)
			if (item?.is_correct) return 'green';
			else return 'silver';
		return 'silver';
	};
	const bulletState = (item: Options) => {
		if (isChosen(item)) return true;
		if (showCorrectAnswer || isStudentFailedRightAnswer)
			if (item?.is_correct) return true;
			else return false;
		return false;
	};

	// A stored answer whose option was edited away or deleted: still the
	// student's answer — render it from the snapshot instead of hiding it.
	const orphan =
		stored &&
		!(question?.multipleChoiceOptions ?? []).some((item) =>
			isChosen(item as Options)
		)
			? stored
			: null;

	return (
		<Spacer p={{ sm: '1rem' }}>
			<QuestionContentWrapper
				dangerouslySetInnerHTML={{ __html: question.content }}
			/>
			<Spacer mb={{ sm: '0.813rem', lg: '2.313rem' }} />
			<FlexLayout px={{ sm: '0.813rem' }} flexDirection="column">
				<AnswersLabel
					fontSize={{ sm: '0.5rem', lg: '1rem' }}
					fontFamily="Roboto"
					color="#000000, 0.6"
				>
					Answers
				</AnswersLabel>
				<Spacer mb={{ sm: '6px', lg: '1rem' }} />
				<FlexLayout flexDirection={'column'}>
					{question?.multipleChoiceOptions?.map((mcqItem, index) => {
						return (
							<FlexLayoutStyle
								alignItems={'center'}
								key={`${mcqItem.id ?? mcqItem.choice}-${index}`}
								mb={{ sm: '0.75rem' }}
								background={getMcqColor(mcqItem)}
							>
								<AnswerBullet
									filled={bulletState(mcqItem)}
									state={bulletColor(mcqItem)}
								/>

								<Spacer mx={{ sm: '4px' }} />
								<Typography fontSize={{ sm: '0.75rem', lg: '1.125rem' }}>
									<Label htmlFor={mcqItem.choice}>{mcqItem.choice}</Label>
								</Typography>
							</FlexLayoutStyle>
						);
					})}
					{orphan && (
						<FlexLayoutStyle
							alignItems={'center'}
							mb={{ sm: '0.75rem' }}
							background={orphan.correct ? 'rgba(0, 214, 107, 0.1)' : '#ffd5cc'}
						>
							<AnswerBullet
								filled
								state={orphan.correct ? 'green' : 'lightRed'}
							/>
							<Spacer mx={{ sm: '4px' }} />
							<Typography fontSize={{ sm: '0.75rem', lg: '1.125rem' }}>
								<Label>
									{orphan.answer} — student's answer (option no longer exists)
								</Label>
							</Typography>
						</FlexLayoutStyle>
					)}
				</FlexLayout>
			</FlexLayout>
			{question.feedback !== null && (
				<>
					<Text>Feedback</Text>
					<FeedbacktWrapper
						dangerouslySetInnerHTML={{ __html: question.feedback as string }}
					/>
				</>
			)}
		</Spacer>
	);
};

export default McqAnswer;

const FlexLayoutStyle = styled(FlexLayout)<{ background: string }>`
	background: ${({ background }) => background};
	height: 2.313rem;
	padding: 0.5rem;
	border-radius: 7px;
	input[type='radio']#correct {
		accent-color: #00d66b;
	}
	input[type='radio']#wrong {
		accent-color: #ff0000;
	}
`;
const Label = styled.label`
	font-size: 0.75rem;
	color: rgba(0, 0, 0, 0.87);
	font-weight: normal;
	@media screen and (min-width: 769px) {
		font-size: 1.125rem;
	}
`;
const AnswerBullet = styled.div<{ state: Color; filled: boolean }>`
	border-radius: 50%;
	position: relative;
	background: ${({ filled, state }) => (filled ? Colors[state] : '')};
	box-shadow: inset 0 0 0 1px #fff;
	outline: 1px solid ${({ filled, state }) => (filled ? Colors[state] : 'gray')};
	width: 0.535rem;
	height: 0.535rem;
	@media screen and (min-width: 769px) {
		width: 1.25rem;
		height: 1.25rem;
		box-shadow: inset 0 0 0 3px #fff;
	}
`;
const AnswersLabel = styled(Typography)`
	color: '#c4c4c4';
	font-size: 1rem;
	font-weight: 300;
	@media screen and (min-width: 769px) {
		margin-bottom: 0.75rem;
	}
`;
